import { ActionReducerMapBuilder } from "@reduxjs/toolkit";

import { ListState } from "@/types/list";

import { ListResponse } from "../../pages/List/schema/list";
import { LeanListsRequestParams, OVERVIEW_REQUEST_SCOPE } from "./lean-lists.params";
import { listApi } from "./list.api";

/**
 * `getLeanLists` overview lifecycle (PREC-4131, ownership PREC-4892).
 *
 * The Lists and Campaigns overview tables both read their rows from the one `lists` slice. Every
 * `getLeanLists` fetch used to write into it, whichever surface issued it — so an unrelated list
 * picker (a Compose recipient picker, an archived-only dropdown, …) could replace the overview's
 * rows and counts with its own, making archived lists reappear on the overview. Only a request that
 * carries the overview's client-only `scope: "overview"` marker may claim or replace the rows now.
 *
 * Among the overview's own requests a tab switch does not abort the previous request, so a slow
 * "My" response landing after the "All" response used to leave the "All" tab showing the "My" rows;
 * and a request that failed (the API answered 500) left the previous tab's rows standing with no
 * error at all. Each overview request stamps its id on `matchPending`; fulfilled applies only the
 * latest, and a rejected latest request empties the rows so the table shows its empty/error state
 * instead of a stale one.
 */

/** The request metadata the slice matchers hand to this lifecycle. */
export interface LeanListsRequestMeta {
	requestId: string;
	arg: { originalArgs?: LeanListsRequestParams };
}

/** True only for the overview's own requests — pickers call `getLeanLists` without the marker. */
export const isOverviewLeanListsRequest = (params?: LeanListsRequestParams): boolean =>
	params?.scope === OVERVIEW_REQUEST_SCOPE;

const ownsOverviewRows = (meta: LeanListsRequestMeta): boolean =>
	isOverviewLeanListsRequest(meta?.arg?.originalArgs);

/** A new request starts: claim the overview via its request id (rows stay until it resolves). */
export const applyGetLeanListsPending = (state: ListState, meta: LeanListsRequestMeta): void => {
	if (!ownsOverviewRows(meta)) {
		return;
	}

	state.latestLeanListsRequestId = meta.requestId;
};

/** A request fulfilled: apply it only if it is still the latest; page 1 replaces, later pages merge. */
export const applyGetLeanListsFulfilled = (
	state: ListState,
	payload: ListResponse | undefined,
	meta: LeanListsRequestMeta
): void => {
	if (!ownsOverviewRows(meta) || meta.requestId !== state.latestLeanListsRequestId) {
		return;
	}

	if (!payload?.data || !payload?.meta) {
		return;
	}

	if (Number(payload.meta.page) === 1) {
		state.lists = payload.data;
	} else {
		const newRows = payload.data.filter((row) => !state.lists.find((p) => p.id === row.id));
		const knownRows = payload.data.filter((row) => state.lists.find((p) => p.id === row.id));
		const savedRows = state.lists.filter((row) => !payload.data.find((p) => p.id === row.id));

		state.lists = [...savedRows, ...knownRows, ...newRows];
	}

	state.listMeta = payload.meta;
};

/** The latest request failed: drop the rows so the table cannot keep showing another tab's data. */
export const applyGetLeanListsRejected = (state: ListState, meta: LeanListsRequestMeta): void => {
	if (!ownsOverviewRows(meta) || meta.requestId !== state.latestLeanListsRequestId) {
		return;
	}

	state.lists = [];
	state.listMeta = { ...state.listMeta, itemCount: 0, pageCount: 0 };
};

/** Wires the lifecycle above onto the list slice (PREC-4892 kept the wiring beside its behaviour). */
export const addLeanListsOverviewMatchers = (builder: ActionReducerMapBuilder<ListState>): void => {
	builder.addMatcher(listApi.endpoints.getLeanLists.matchPending, (state, { meta }) =>
		applyGetLeanListsPending(state, meta)
	);
	builder.addMatcher(listApi.endpoints.getLeanLists.matchFulfilled, (state, { payload, meta }) =>
		applyGetLeanListsFulfilled(state, payload, meta)
	);
	builder.addMatcher(listApi.endpoints.getLeanLists.matchRejected, (state, { meta }) =>
		applyGetLeanListsRejected(state, meta)
	);
};
